// TOUR-9 — Order Intake walkthrough (sales-rep submit + ops review awareness).

import type { FireflyTourArtifact } from "../tourTypes";

export const SALES_INTAKE_TOUR_ID = "tour-sales-intake";

export const SALES_INTAKE_TOUR: FireflyTourArtifact = {
  id: SALES_INTAKE_TOUR_ID,
  version: 1,
  defaultIntent: "teaching",
  intents: [
    "order intake tour",
    "sales intake tour",
    "how do i submit an order",
    "submit an order",
    "send deal to manufacturing",
    "how do i submit a customer order",
    "submit as deal",
    "order intake walkthrough",
  ],
  bucketRelevance: ["sales"],
  requiredRoles: ["sales", "sales_rep", "ops_reviewer"],
  steps: [
    {
      id: "nav-sales-intake",
      target: { mode: "leads", anchorId: "mode-nav-salesIntake", scrollIntoView: true },
      deferModeNavigation: true,
      skipWhenCurrentMode: "salesIntake",
      copy: [
        {
          roles: ["sales", "sales_rep"],
          title: "Open Order Intake",
          body: "Formal customer orders start here after capture or an opportunity. Tap Order Intake in the sidebar (or bottom bar on mobile).",
        },
        {
          roles: ["ops_reviewer"],
          title: "Open Order Intake",
          body: "Review incoming order snapshots here — same mode reps use to submit. Tap Order Intake in the sidebar (or bottom bar on mobile).",
        },
      ],
      waitForClick: true,
    },
    {
      id: "intake-surface",
      target: { mode: "salesIntake", anchorId: "intake-surface", scrollIntoView: true },
      copy: [
        {
          roles: ["sales", "sales_rep"],
          title: "Sales rep view",
          body: "Submit new orders from this variant. Multi-role users: use View as to switch — Ops review and Manufacturer accept are separate variants on the same mode.",
        },
        {
          roles: ["ops_reviewer"],
          title: "Ops review view",
          body: "Paperwork-check queue lives on the Ops review variant. Switch View as → Sales rep to walk the submit path; stay on Ops review for accept / hold decisions.",
        },
      ],
      advanceOn: "next_button",
    },
    {
      id: "intake-form",
      target: { mode: "salesIntake", anchorId: "intake-form", scrollIntoView: true },
      copy: [
        {
          roles: ["sales", "sales_rep"],
          title: "Submit customer order",
          body: "Fill customer details, sale path, and location. Prefill from Leads appears when you use Submit as deal on a captured lead.",
        },
        {
          roles: ["ops_reviewer"],
          title: "Submit form (Sales rep variant)",
          body: "This anchor is on the Sales rep form — switch View as → Sales rep to highlight it. Ops reviewers normally work the review queue instead of submitting.",
        },
      ],
      advanceOn: "next_button",
    },
    {
      id: "submit-order",
      target: { mode: "salesIntake", anchorId: "intake-submit-order", scrollIntoView: true },
      copy: [
        {
          roles: ["sales", "sales_rep"],
          title: "Submit order",
          body: "Submit order sends the snapshot for review — internal org queue or upstream manufacturer. You stay in control of when it goes out.",
        },
        {
          roles: ["ops_reviewer"],
          title: "Submit order (Sales rep)",
          body: "Reps tap this to enqueue a snapshot. As Ops review, you accept or hold from the review queue — not from this button.",
        },
      ],
      destructive: true,
    },
    {
      id: "submitted-list",
      target: { mode: "salesIntake", anchorId: "intake-submitted-list", scrollIntoView: true },
      copy: [
        {
          roles: ["sales", "sales_rep"],
          title: "Your submissions",
          body: "Track pending review and on-hold orders below the form. Ops reviewers accept or hold from their Order Intake variant.",
        },
        {
          roles: ["ops_reviewer"],
          title: "Rep submissions list",
          body: "Reps see their own pending rows here. For the org-wide review queue, switch View as → Ops review on the next step.",
        },
      ],
      advanceOn: "next_button",
    },
    {
      id: "review-queue",
      target: { mode: "salesIntake", anchorId: "intake-review-queue", scrollIntoView: true },
      copy: [
        {
          roles: ["ops_reviewer"],
          title: "Ops review queue",
          body: "Pending review rows appear here. Select a row to Accept, Hold, or Cancel — post-acceptance deal paperwork lives in Ops → operations.",
        },
        {
          roles: ["sales", "sales_rep"],
          title: "Ops review queue",
          body: "Ops reviewers work this queue on the Ops review variant. Switch View as if you also wear the reviewer hat.",
        },
      ],
      advanceOn: "next_button",
    },
    {
      id: "ask-firefly",
      target: { mode: "salesIntake", anchorId: "ask-firefly", scrollIntoView: true },
      copy: {
        title: "Ask Firefly anytime",
        body: "Need opportunities or capture help? Ask Firefly anytime — tours can start from any Sales mode. Manufacturer accept uses the same mode (View as → Manufacturer).",
      },
      advanceOn: "next_button",
    },
  ],
};
